if (!customElements.get('tpt-review-5-mobile-carousel')) {
  customElements.define(
    'tpt-review-5-mobile-carousel',
    class TptReview5MobileCarousel extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 1;
        this.isExpanded = false;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.isAnimating = false;
        this.autoPlayTimer = null;
      }

      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('.tpt-review-5-carousel-card'));
        this.dotsContainer = this.querySelector('.tpt-review-5-carousel-dots');
        this.overlay = this.querySelector('.tpt-review-5-carousel-overlay');
        
        if (!this.cards.length) return;

        this.setupNavigation();
        this.setupDots();
        this.setupTouch();
        this.setupCardClick();
        this.updateCarousel();

        const autoPlay = this.dataset.autoPlay === 'true';
        const delay = parseInt(this.dataset.delay, 10) * 1000 || 3000;

        if (autoPlay && this.cards.length > 1) {
          this.startAutoPlay(delay);
        }
      }

      startAutoPlay(delay) {
        this.stopAutoPlay();

        this.autoPlayTimer = setInterval(() => {
          if (!this.isExpanded && !this.isAnimating) {
            this.next();
          }
        }, delay);

        let autoPlayDelay;
        this.addEventListener('touchstart', () => {
          this.stopAutoPlay();
          clearTimeout(autoPlayDelay);

          autoPlayDelay = setTimeout(() => {
            this.startAutoPlay(delay);
          }, delay);
        });
      }

      stopAutoPlay() {
        if (this.autoPlayTimer) {
          clearInterval(this.autoPlayTimer);
          this.autoPlayTimer = null;
        }
      }

      setupNavigation() {
        const prevBtn = this.querySelector('.tpt-review-5-carousel-nav.prev');
        const nextBtn = this.querySelector('.tpt-review-5-carousel-nav.next');

        if (prevBtn) {
          prevBtn.addEventListener('click', () => this.prev());
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', () => this.next());
        }
      }

      setupDots() {
        if (!this.dotsContainer) return;

        this.dotsContainer.innerHTML = '';
        this.cards.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.className = 'tpt-review-5-carousel-dot';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          
          if (index === this.currentIndex) {
            dot.classList.add('active');
          }
          
          dot.addEventListener('click', () => this.slideToDot(index));
          this.dotsContainer.appendChild(dot);
        });
      }

      setupTouch() {
        const container = this.querySelector('.tpt-review-5-carousel-container');
        if (!container) return;

        container.addEventListener('touchstart', (e) => {
          if (this.isExpanded) return;
          this.touchStartX = e.touches[0].clientX;
          this.touchEndX = this.touchStartX;
        }, { passive: true });

        container.addEventListener('touchmove', (e) => {
          if (this.isExpanded) return;
          this.touchEndX = e.touches[0].clientX;
        }, { passive: true });

        container.addEventListener('touchend', () => {
          if (this.isExpanded) return;

          const distance = this.touchStartX - this.touchEndX;
          const minSwipeDistance = 50;

          if (Math.abs(distance) >= minSwipeDistance) {
            this.handleSwipe();
          }

          this.touchStartX = 0;
          this.touchEndX = 0;
        });
      }

      setupCardClick() {
        this.cards.forEach((card, index) => {
          card.addEventListener('click', (e) => {
            if (this.isExpanded) return;

            const cardIndex = this.cards.indexOf(card);
            if (cardIndex === this.currentIndex) this.expandCard(card);
            else this.goTo(cardIndex);
          });

          const closeBtn = card.querySelector('.tpt-review-5-carousel-close');
          if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              this.closeExpanded();
            });
          }
        });

        if (this.overlay) {
          this.overlay.addEventListener('click', () => {
            this.closeExpanded();
          });
        }
      }

      handleSwipe() {
        const minSwipeDistance = 50;
        const distance = this.touchStartX - this.touchEndX;

        if (Math.abs(distance) < minSwipeDistance) return;

        if (distance > 0) {
        this.next();
        } else {
        this.prev();
        }

        this.touchStartX = 0;
        this.touchEndX = 0;
      }

      prev() {
        if (this.isExpanded || this.isAnimating) return;
        this.currentIndex = (this.currentIndex - 1 + this.cards.length) % this.cards.length;
        this.updateCarousel();
      }

      next() {
        if (this.isExpanded || this.isAnimating) return;
        this.currentIndex = (this.currentIndex + 1) % this.cards.length;
        this.updateCarousel();
      }

      goTo(index) {
        if (this.isExpanded || this.isAnimating) return;
        this.currentIndex = index;
        this.updateCarousel();
      }

      updateCarousel(ignoreAnimating = false) {
        if (this.isAnimating && !ignoreAnimating && !this.isExpanded) return;
        this.isAnimating = true;

        this.cards.forEach((card, index) => {
          card.classList.remove('center', 'left', 'right', 'hidden');
          const diff = index - this.currentIndex;
          const totalCards = this.cards.length;

          if (diff === 0) card.classList.add('center');
          else if (diff === -1 || diff === totalCards - 1) card.classList.add('left');
          else if (diff === 1 || diff === -(totalCards - 1)) card.classList.add('right');
          else card.classList.add('hidden');
        });

        this.updateDots();

        const transitionDuration = 400;
        setTimeout(() => {
            this.isAnimating = false;
        }, transitionDuration);
      }

      slideToDot(targetIndex) {
        if (this.isExpanded || this.isSlidingDot) return;
        if (targetIndex === this.currentIndex) return;

        const total = this.cards.length;
        let diff = (targetIndex - this.currentIndex + total) % total;
        let step = diff > total / 2 ? -1 : 1;

        const moveStep = () => {
          if (this.currentIndex === targetIndex) {
            this.isSlidingDot = false;
            return;
          }

          this.currentIndex = (this.currentIndex + step + total) % total;
          this.updateCarousel(true);

          setTimeout(moveStep, 150); 
        };

        this.isSlidingDot = true;
        moveStep();
      }

      updateDots() {
        const dots = this.dotsContainer?.querySelectorAll('.tpt-review-5-carousel-dot');
        if (!dots) return;

        dots.forEach((dot, index) => {
          dot.classList.toggle('active', index === this.currentIndex);
        });
      }

      expandCard(card) {
        this.isExpanded = true;
        card.classList.add('expanded');
        
        if (this.overlay) {
          this.overlay.classList.add('active');
        }

        document.body.style.overflow = 'hidden';
      }

      closeExpanded() {
        this.isExpanded = false;
        
        this.cards.forEach(card => {
          card.classList.remove('expanded');
        });

        if (this.overlay) {
          this.overlay.classList.remove('active');
        }

        document.body.style.overflow = '';

        this.updateCarousel();
      }
    }
  );
}